/**
 * Sign-in overlay shown before joining the mall: "Sign in with Google" (via
 * Google Identity Services) or "Continue as guest". Google returns an ID
 * token in-page; the caller exchanges it through the server's /api/login
 * endpoints. Guest skips everything, exactly the pre-login behavior.
 */

interface GoogleIdApi {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
      }): void;
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
    };
  };
}

export type LoginChoice =
  | { kind: "google"; googleIdToken: string }
  | { kind: "password"; email: string; password: string }
  | { kind: "guest" };

const GSI_SRC: string = "https://accounts.google.com/gsi/client";

export class LoginScreen {
  private readonly el: HTMLElement;
  private readonly googleClientId: string | undefined;

  constructor(elementId: string, googleClientId: string | undefined) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`Login element #${elementId} not found`);
    }
    this.el = found;
    this.googleClientId = googleClientId;
  }

  /** Show the overlay and resolve with the player's choice. */
  choose(statusText?: string): Promise<LoginChoice> {
    return new Promise<LoginChoice>((resolve) => {
      this.el.innerHTML = `
        <div class="ls-card">
          <h1>Mall Mart</h1>
          <p class="ls-sub"></p>
          <div class="ls-google"></div>
          <div class="ls-divider">or use your Perilous Games account</div>
          <form class="ls-form">
            <input class="ls-email" type="email" placeholder="Email" autocomplete="email" required />
            <input class="ls-password" type="password" placeholder="Password" autocomplete="current-password" required />
            <button class="ls-submit" type="submit">Sign in</button>
          </form>
          <button class="ls-guest" type="button">Continue as guest</button>
          <p class="ls-note" hidden></p>
        </div>`;
      const sub: HTMLElement = this.el.querySelector(".ls-sub") as HTMLElement;
      sub.textContent = statusText ?? "Sign in to keep your cart and name across visits";
      this.el.hidden = false;

      const guestBtn: HTMLButtonElement = this.el.querySelector(".ls-guest") as HTMLButtonElement;
      guestBtn.addEventListener("click", () => {
        this.hide();
        resolve({ kind: "guest" });
      });

      const form: HTMLFormElement = this.el.querySelector(".ls-form") as HTMLFormElement;
      form.addEventListener("submit", (e: Event) => {
        e.preventDefault();
        const email: string = (this.el.querySelector(".ls-email") as HTMLInputElement).value.trim();
        const password: string = (this.el.querySelector(".ls-password") as HTMLInputElement).value;
        if (email === "" || password === "") {
          return;
        }
        this.hide();
        resolve({ kind: "password", email, password });
      });

      const googleHost: HTMLElement = this.el.querySelector(".ls-google") as HTMLElement;
      const note: HTMLElement = this.el.querySelector(".ls-note") as HTMLElement;
      if (this.googleClientId === undefined || this.googleClientId === "") {
        note.textContent = "Google sign-in is not configured; playing as guest.";
        note.hidden = false;
        return;
      }
      this.loadGsi()
        .then((google: GoogleIdApi) => {
          google.accounts.id.initialize({
            client_id: this.googleClientId as string,
            callback: (response: { credential: string }) => {
              this.hide();
              resolve({ kind: "google", googleIdToken: response.credential });
            },
          });
          google.accounts.id.renderButton(googleHost, {
            theme: "filled_black",
            size: "large",
            shape: "pill",
            text: "signin_with",
          });
        })
        .catch(() => {
          note.textContent = "Google sign-in failed to load; you can still play as guest.";
          note.hidden = false;
        });
    });
  }

  hide(): void {
    this.el.hidden = true;
    this.el.innerHTML = "";
  }

  private loadGsi(): Promise<GoogleIdApi> {
    const existing = (window as unknown as { google?: GoogleIdApi }).google;
    if (existing !== undefined) {
      return Promise.resolve(existing);
    }
    return new Promise<GoogleIdApi>((resolve, reject) => {
      const script: HTMLScriptElement = document.createElement("script");
      script.src = GSI_SRC;
      script.async = true;
      script.onload = () => {
        const google = (window as unknown as { google?: GoogleIdApi }).google;
        if (google === undefined) {
          reject(new Error("GSI loaded without window.google"));
          return;
        }
        resolve(google);
      };
      script.onerror = () => reject(new Error("GSI script failed to load"));
      document.head.appendChild(script);
    });
  }
}
